import { type ColorRamp, hexToRgb } from "@offstreetparking/domain/value/branding";
import type { RampStop } from "./generate-ramp";

// Hand-authored Offstreet brand ramps (source: design tokens.json). These are the
// single source of truth for "the Offstreet default look" — consumed both as the
// dashboard's no-branding theme fallback (ThemeProvider) and as the default/reset
// ramp in the branding form. They are intentionally NOT the algorithmic output of
// `buildRampFromHex(base)`: the design team tuned individual stops (e.g. the primary
// .600 surface is a brighter green than a generated ramp would produce), so a company
// with no stored ramps renders the same picked colors everywhere.
//
// Primary omits the 150 stop — matches PRIMARY_RAMP_KEYS / STOPS_BY_ROLE.primary,
// which leave 150 unset because the primary CSS template never reads it.

export const OFFSTREET_PRIMARY_BRAND_RAMP: ColorRamp = {
	base: "#06653C",
	stops: {
		100: "#F2FFF3",
		200: "#C0EDC5",
		300: "#6AE693",
		400: "#46D17E",
		500: "#00AC69",
		600: "#008553",
		700: "#06653C",
		800: "#03422A",
		900: "#001C11",
	},
	overrides: [],
};

export const OFFSTREET_ACCENT_BRAND_RAMP: ColorRamp = {
	base: "#146EEB",
	stops: {
		100: "#F6FAFF",
		150: "#D7E7FB",
		200: "#B0CFF8",
		300: "#7CAEF4",
		400: "#488EF0",
		500: "#146EEB",
		600: "#0F55B7",
		700: "#0B3D83",
		800: "#07254E",
		900: "#020C1A",
	},
	overrides: [],
};

// DS injects CSS as `rgb(var(--os-color-*))` triplets; derive them from the hex ramps
// above so the picked colors never drift between the two representations.
const toRgbRamp = (ramp: ColorRamp): Record<RampStop, string> => {
	const out = {} as Record<RampStop, string>;
	for (const [stop, hex] of Object.entries(ramp.stops)) {
		const { r, g, b } = hexToRgb(hex);
		out[Number(stop) as RampStop] = `${r}, ${g}, ${b}`;
	}
	return out;
};

export const OFFSTREET_PRIMARY_RAMP: Record<RampStop, string> = toRgbRamp(OFFSTREET_PRIMARY_BRAND_RAMP);
export const OFFSTREET_ACCENT_RAMP: Record<RampStop, string> = toRgbRamp(OFFSTREET_ACCENT_BRAND_RAMP);
